import React, { useCallback, useLayoutEffect } from 'react'
import { Overlay } from 'react-bootstrap'
import type { OverlayProps } from 'react-bootstrap'
import type { RefObject } from 'react'
import type { BubbleOffset } from './useChatBubbleOffset'
import type { ChatModalProps } from './ChatModal'
import { ChatModal } from './ChatModal'
import { mergeRefs } from './mergeRefs'

// Gap between the bubble and the chat window edge.
const CHAT_WINDOW_GAP_PX = 12
// Minimum space kept between the chat window and the viewport edge.
const VIEWPORT_MARGIN_PX = 8

// top/left instead of transform: the .modalOpen/.modalClosed keyframes animate transform and would override it
const CHAT_WINDOW_POPPER_CONFIG: OverlayProps['popperConfig'] = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'flip',
      options: { fallbackPlacements: ['bottom-end', 'left', 'right'], altAxis: false, padding: VIEWPORT_MARGIN_PX },
    },
    { name: 'preventOverflow', options: { altAxis: true, tether: false, padding: VIEWPORT_MARGIN_PX } },
    { name: 'computeStyles', options: { gpuAcceleration: false, adaptive: false } },
  ],
}

export type AnchoredChatModalProps = ChatModalProps & {
  /** Draggable bubble the chat window opens beside. */
  bubbleRef: RefObject<HTMLButtonElement>
  /** Current drag translation, watched so the window re-anchors after a reset. */
  bubbleOffset: BubbleOffset
}

export const AnchoredChatModal = ({ bubbleRef, bubbleOffset, ...chatModalProps }: AnchoredChatModalProps) => (
  <Overlay
    show
    target={bubbleRef}
    placement="top-end"
    flip
    offset={[0, CHAT_WINDOW_GAP_PX]}
    transition={false}
    popperConfig={CHAT_WINDOW_POPPER_CONFIG}
  >
    {({ ref, style, popper }) => (
      <PositionedChatModal
        {...chatModalProps}
        overlayRef={ref}
        overlayStyle={style}
        reposition={popper?.scheduleUpdate}
        bubbleOffset={bubbleOffset}
      />
    )}
  </Overlay>
)

type PositionedChatModalProps = ChatModalProps & {
  overlayRef: React.RefCallback<HTMLElement>
  overlayStyle: React.CSSProperties
  reposition?: () => void
  bubbleOffset: BubbleOffset
}

const PositionedChatModal = React.forwardRef<HTMLElement, PositionedChatModalProps>(
  ({ overlayRef, overlayStyle, reposition, bubbleOffset, ...chatModalProps }, ref) => {
    const { isOpen, isMaximized, width, height } = chatModalProps

    // Popper recomputes only on scroll and resize — re-anchor when the window
    // opens from display:none, the bubble moves, or the window size changes.
    useLayoutEffect(() => {
      reposition?.()
    }, [reposition, isOpen, isMaximized, width, height, bubbleOffset.x, bubbleOffset.y])

    // The transition layer clones this element with a ref that has nowhere to
    // attach on a plain function component — forward it to the window node
    // alongside the Popper positioning ref.
    const setRefs = useCallback(
      (node: HTMLElement | null) => {
        mergeRefs(node, overlayRef, ref)
      },
      [overlayRef, ref]
    )

    return <ChatModal {...chatModalProps} ref={setRefs} anchorStyle={overlayStyle} />
  }
)
